import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { subscriptionApi } from '@/services/api/subscriptions'
import { accessLimitService, UserAccessLimits } from '@/services/accessLimitService'
import { SubscriptionResponse, SubscriptionPlan, PaymentHistoryItem } from '@/types/subscription'
import { showSnackbar } from './uiSlice'

interface SubscriptionState {
    currentSubscription: SubscriptionResponse | null
    plans: SubscriptionPlan[]
    paymentHistory: PaymentHistoryItem[]
    isLoading: boolean
    isProcessingPayment: boolean
    error: string | null
    paymentReference: string | null
    paymentAuthorizationUrl: string | null
    totalPaymentPages: number
    totalPaymentCount: number
    accessLimits: UserAccessLimits | null
    accessLimitsFetchedAt: string | null
    isAccessLimitsLoading: boolean
    accessLimitsError: string | null
}

const initialState: SubscriptionState = {
    currentSubscription: null,
    plans: [],
    paymentHistory: [],
    isLoading: false,
    isProcessingPayment: false,
    error: null,
    paymentReference: null,
    paymentAuthorizationUrl: null,
    totalPaymentPages: 0,
    totalPaymentCount: 0,
    accessLimits: null,
    accessLimitsFetchedAt: null,
    isAccessLimitsLoading: false,
    accessLimitsError: null,
}

// Async thunks
export const fetchPlans = createAsyncThunk(
    'subscription/fetchPlans',
    async (_, { rejectWithValue }) => {
        try {
            const response = await subscriptionApi.getPlans()
            return response.data.plans || []
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch plans')
        }
    }
)

export const fetchCurrentSubscription = createAsyncThunk(
    'subscription/fetchCurrent',
    async (_, { rejectWithValue }) => {
        try {
            const response = await subscriptionApi.getCurrentSubscription()
            return response.data.subscription || null
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch subscription')
        }
    }
)

export const fetchAccessLimits = createAsyncThunk<
    { accessLimits: UserAccessLimits; fetchedAt: string },
    void,
    { rejectValue: { message: string; aborted?: boolean } }
>(
    'subscription/fetchAccessLimits',
    async (_, { rejectWithValue }) => {
        try {
            const accessLimits = await accessLimitService.getUserAccessLimits()
            return {
                accessLimits,
                fetchedAt: new Date().toISOString(),
            }
        } catch (error: any) {
            const isAborted = error?.name === 'AbortError' || error?.code === 'ERR_CANCELED' || error?.code === 'ECONNABORTED'
            const message = error?.response?.data?.message || error?.message || 'Failed to fetch access limits'
            return rejectWithValue({ message, aborted: isAborted })
        }
    }
)

export const initializePayment = createAsyncThunk(
    'subscription/initializePayment',
    async (
        data: { tier: string; currency?: string; redirectUrl?: string },
        { rejectWithValue, dispatch }
    ) => {
        try {
            const response = await subscriptionApi.initializePayment({
                tier: data.tier as any,
                currency: data.currency as any,
                redirectUrl: data.redirectUrl,
            })

            return response.data
        } catch (error: any) {
            const message = error.response?.data?.message || 'Payment initialization failed'
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const verifyPayment = createAsyncThunk(
    'subscription/verifyPayment',
    async (reference: string, { rejectWithValue, dispatch }) => {
        try {
            const response = await subscriptionApi.verifyPayment({ reference })
            dispatch(showSnackbar({
                message: 'Payment verified successfully!',
                type: 'success'
            }))
            return response.data
        } catch (error: any) {
            const message = error.response?.data?.message || 'Payment verification failed'
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const upgradeTier = createAsyncThunk(
    'subscription/upgradeTier',
    async (
        data: { targetTier: string; currency?: string },
        { rejectWithValue, dispatch }
    ) => {
        try {
            const response = await subscriptionApi.upgradeTier({
                targetTier: data.targetTier as any,
                currency: data.currency as any,
            })

            dispatch(showSnackbar({
                message: 'Subscription upgraded successfully!',
                type: 'success'
            }))
            return response.data
        } catch (error: any) {
            const message = error.response?.data?.message || 'Upgrade failed'
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const cancelSubscription = createAsyncThunk(
    'subscription/cancel',
    async (reason: string, { rejectWithValue, dispatch }) => {
        try {
            const response = await subscriptionApi.cancelSubscription({ reason })
            dispatch(showSnackbar({
                message: 'Subscription cancelled successfully',
                type: 'success'
            }))
            return response.data.message
        } catch (error: any) {
            const message = error.response?.data?.message || 'Cancellation failed'
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const fetchPaymentHistory = createAsyncThunk(
    'subscription/fetchPaymentHistory',
    async (params: { page?: number; limit?: number } | undefined, { rejectWithValue }) => {
        try {
            const response = await subscriptionApi.getPaymentHistory(params)
            const data = response.data || {}
            return {
                payments: data.payments || [],
                totalCount: data.totalCount || 0,
                totalPages: data.totalPages || 0,
                currentPage: data.currentPage || 1,
            }
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch payment history')
        }
    }
)

const subscriptionSlice = createSlice({
    name: 'subscription',
    initialState,
    reducers: {
        setPaymentReference: (state, action: PayloadAction<string>) => {
            state.paymentReference = action.payload
        },
        clearPaymentReference: (state) => {
            state.paymentReference = null
            state.paymentAuthorizationUrl = null
        },
        clearError: (state) => {
            state.error = null
        },
    },
    extraReducers: (builder) => {
        // Access Limits
        builder
            .addCase(fetchAccessLimits.pending, (state) => {
                state.isAccessLimitsLoading = true
                state.accessLimitsError = null
            })
            .addCase(fetchAccessLimits.fulfilled, (state, action) => {
                state.isAccessLimitsLoading = false
                state.accessLimits = action.payload.accessLimits
                state.accessLimitsFetchedAt = action.payload.fetchedAt
            })
            .addCase(fetchAccessLimits.rejected, (state, action) => {
                state.isAccessLimitsLoading = false
                const payload = action.payload as { message: string; aborted?: boolean } | undefined
                if (payload?.aborted) {
                    return
                }
                state.accessLimitsError = payload?.message || action.error.message || 'Failed to fetch access limits'
            })

        // Fetch Plans
        builder
            .addCase(fetchPlans.pending, (state) => {
                state.isLoading = true
            })
            .addCase(fetchPlans.fulfilled, (state, action) => {
                state.isLoading = false
                state.plans = action.payload
            })
            .addCase(fetchPlans.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Fetch Current Subscription
        builder
            .addCase(fetchCurrentSubscription.pending, (state) => {
                state.isLoading = true
            })
            .addCase(fetchCurrentSubscription.fulfilled, (state, action) => {
                state.isLoading = false
                state.currentSubscription = action.payload
            })
            .addCase(fetchCurrentSubscription.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Initialize Payment
        builder
            .addCase(initializePayment.pending, (state) => {
                state.isProcessingPayment = true
                state.error = null
                state.paymentAuthorizationUrl = null
            })
            .addCase(initializePayment.fulfilled, (state, action) => {
                state.isProcessingPayment = false
                state.paymentReference = action.payload.reference
                state.paymentAuthorizationUrl = action.payload.authorizationUrl || null
            })
            .addCase(initializePayment.rejected, (state, action) => {
                state.isProcessingPayment = false
                state.error = action.payload as string
                state.paymentAuthorizationUrl = null
            })

        // Verify Payment
        builder
            .addCase(verifyPayment.pending, (state) => {
                state.isProcessingPayment = true
            })
            .addCase(verifyPayment.fulfilled, (state, action) => {
                state.isProcessingPayment = false
                state.currentSubscription = action.payload.subscription
                state.paymentReference = null
                state.paymentAuthorizationUrl = null
            })
            .addCase(verifyPayment.rejected, (state, action) => {
                state.isProcessingPayment = false
                state.error = action.payload as string
                state.paymentAuthorizationUrl = null
            })

        // Upgrade Tier
        builder
            .addCase(upgradeTier.pending, (state) => {
                state.isProcessingPayment = true
                state.error = null
                state.paymentAuthorizationUrl = null
            })
            .addCase(upgradeTier.fulfilled, (state, action) => {
                state.isProcessingPayment = false
                if (action.payload?.subscription) {
                    state.currentSubscription = action.payload.subscription
                }
            })
            .addCase(upgradeTier.rejected, (state, action) => {
                state.isProcessingPayment = false
                state.error = action.payload as string
                state.paymentAuthorizationUrl = null
            })

        // Cancel Subscription
        builder
            .addCase(cancelSubscription.pending, (state) => {
                state.isLoading = true
            })
            .addCase(cancelSubscription.fulfilled, (state) => {
                state.isLoading = false
                if (state.currentSubscription) {
                    state.currentSubscription.status = 'cancelled'
                }
            })
            .addCase(cancelSubscription.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Fetch Payment History
        builder
            .addCase(fetchPaymentHistory.pending, (state) => {
                state.isLoading = true
            })
            .addCase(fetchPaymentHistory.fulfilled, (state, action) => {
                state.isLoading = false
                state.paymentHistory = action.payload.payments
                state.totalPaymentCount = action.payload.totalCount
                state.totalPaymentPages = action.payload.totalPages
            })
            .addCase(fetchPaymentHistory.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })
    },
})

export const { setPaymentReference, clearPaymentReference, clearError } = subscriptionSlice.actions
export default subscriptionSlice.reducer